import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import TeamHeroSection from "./TeamHeroSection";
import TeamOverviewTabs from "./TeamOverviewTabs";
import OverviewTab from "./OverviewTab";
import ImpactLoadTab from "./ImpactLoadTab";
import AttendanceTab from "./AttendanceTab";
import ConsentTab from "./ConsentTab";
import AddPlayerModal from "../AddPlayerModal";
import EditPlayerModal from "../EditPlayerModal";
import DeletePlayerModal from "../DeletePlayerModal";
import SwitchTeamModal from "../SwitchTeamModal";
import {
  getDefaultPeriod,
  formatDateParam,
} from "components/common/PeriodPicker/periodUtils";
import betaAPI from "v2/api";
import { dateLocale } from "i18n";
import showToast from "utils/toast";
import { teamTracksHeaders } from "v2/utils/headers";
import { impactLoadStats, teamLoadPoint } from "v2/utils/impactLoad";
import PaneFrame, { PageFrame } from "../common/PaneFrame";

// Map API trend values to component values
const mapTrend = (trend) => {
  switch (trend) {
    case "increasing":
      return "up";
    case "decreasing":
      return "down";
    default:
      return "stable";
  }
};

// Map API status values to component values
const mapStatus = (status) => {
  switch (status) {
    case "high":
      return "review";
    case "low":
      return "monitoring";
    default:
      return "active";
  }
};

// One overview-API player → the list's shape. Shared by the initial fetch and
// load-more so appended pages can't drift from page 1's mapping.
const mapPlayer = (p, fallbackAvgImpacts) => ({
  id: p.id,
  name: p.name,
  age: p.age,
  position: p.position,
  seasonLoad: p.avg_load ?? 0,
  loadVsPeers: p.load_vs_peers ?? null,
  peerAvgLoad: p.peer_avg_load ?? null,
  peerSessions: p.peer_sessions ?? 0,
  events: p.total_impacts ?? 0,
  peerAvgEvents: p.peer_avg_events ?? Math.round(fallbackAvgImpacts),
  trend: mapTrend(p.trend),
  status: mapStatus(p.status),
  consentStatus: p.consent_status ?? "not_required",
  deletionScheduledFor: p.deletion_scheduled_for ?? null,
});

const PLAYERS_PAGE_SIZE = 50;

const TABS = [
  { id: "overview", labelKey: "team.overview.tabs.overview" },
  { id: "impact-load", labelKey: "team.overview.tabs.impactLoad" },
  { id: "attendance", labelKey: "team.overview.tabs.attendance" },
];

const TeamOverview = () => {
  const { t } = useTranslation();
  const { workspaceId, teamId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Overview first, unless the link points at an athlete (?player=…), whose
  // detail lives on the Impact load tab.
  const [activeTab, setActiveTab] = useState(() =>
    searchParams.get("player") ? "impact-load" : "overview"
  );
  const [period, setPeriod] = useState(() => getDefaultPeriod());
  const [sessionType, setSessionType] = useState("all");

  // Get initial player ID from URL query param
  const playerIdFromUrl = searchParams.get("player");
  const [selectedPlayerId, setSelectedPlayerId] = useState(playerIdFromUrl);
  const [sortedPlayers, setSortedPlayers] = useState([]);
  const [showAddPlayerModal, setShowAddPlayerModal] = useState(false);
  const [showEditPlayerModal, setShowEditPlayerModal] = useState(false);
  const [showDeletePlayerModal, setShowDeletePlayerModal] = useState(false);
  const [showSwitchTeamModal, setShowSwitchTeamModal] = useState(false);

  // Sync URL param → state when navigating on the same page
  useEffect(() => {
    const playerFromUrl = searchParams.get("player");
    if (playerFromUrl && playerFromUrl !== selectedPlayerId) {
      setSelectedPlayerId(playerFromUrl);
      setActiveTab("impact-load");
    }
  }, [searchParams]);

  // API data state
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState(null);
  const [players, setPlayers] = useState([]);
  // Server pagination of the athletes list ({ totalCount, page, totalPages });
  // the list infinite-scrolls through pages, the count chip shows totalCount.
  const [playersMeta, setPlayersMeta] = useState({
    totalCount: 0,
    page: 1,
    totalPages: 1,
  });
  const [playersLoadingMore, setPlayersLoadingMore] = useState(false);
  const teamAvgImpactsRef = useRef(0);
  const [teamStats, setTeamStats] = useState(null);
  const [teamLoadData, setTeamLoadData] = useState([]);
  const [playerLoadData, setPlayerLoadData] = useState([]);
  // Which player `playerLoadData` belongs to. Selecting a player flips the
  // view to skeleton on the very same render (before the fetch effect runs),
  // so the previous player's numbers never flash through.
  const [playerLoadFor, setPlayerLoadFor] = useState(null);
  // True while the selected player's load chart is being (re)fetched.
  const [playerLoading, setPlayerLoading] = useState(false);
  // Team-wide header aggregation (computed client-side from session impacts —
  // the API has no header data). { total, perSession, loading }.
  const [headerStats, setHeaderStats] = useState({
    total: 0,
    perSession: [],
    loading: false,
  });

  const tracksHeaders = teamTracksHeaders({ sport: team?.sport, teamId });

  // Period-only params. The load charts use these and are filtered by
  // Training/Match client-side (see filteredTeamLoadData), so they don't need a
  // server round-trip on every type toggle — and stay cache-hits across toggles.
  const periodParams = useMemo(() => {
    const params = {};
    if (period?.startDate) {
      params.start_date = formatDateParam(period.startDate);
    }
    if (period?.endDate) {
      params.end_date = formatDateParam(period.endDate);
    }
    return params;
  }, [period]);

  // Period + type. The overview aggregates and header counts are scoped
  // server-side by session_type, so those endpoints need it.
  const filterParams = useMemo(() => {
    const params = { ...periodParams };
    if (sessionType !== "all") {
      params.session_type = sessionType;
    }
    return params;
  }, [periodParams, sessionType]);

  // Team identity (name/sport/roster size) is period-independent, so it's
  // fetched once per team rather than on every period/type change.
  const fetchTeam = useCallback(async () => {
    if (!workspaceId || !teamId) return;
    try {
      const res = await betaAPI.getTeam({ workspaceId, teamId });
      const teamData = res.data;
      setTeam({
        id: teamId,
        name: teamData.name || t("team.overview.fallbackTeamName"),
        sport: teamData.sport || "",
        under16: !!teamData.under_16,
        playerCount: teamData.player_count ?? 0,
      });
    } catch (err) {
      console.error("Failed to fetch team", err);
      // Fall back to a minimal record so the page still renders.
      setTeam(
        (prev) =>
          prev || {
            id: teamId,
            name: t("team.overview.fallbackTeamName"),
            sport: "",
            playerCount: 0,
          }
      );
    }
  }, [workspaceId, teamId, t]);

  useEffect(() => {
    fetchTeam();
  }, [fetchTeam]);

  // Parent sign-ups waiting for the coach (under-16 teams only).
  const [pendingClaims, setPendingClaims] = useState(0);
  const fetchPendingClaims = useCallback(async () => {
    if (!workspaceId || !teamId || !team?.under16) return;
    try {
      const { data } = await betaAPI.getGuardianClaims({ workspaceId, teamId });
      setPendingClaims(data.claims.length);
    } catch (err) {
      console.error("Failed to load parent sign-ups", err);
    }
  }, [workspaceId, teamId, team?.under16]);

  useEffect(() => {
    fetchPendingClaims();
  }, [fetchPendingClaims]);

  // Fetch team overview + load chart
  const fetchTeamData = useCallback(
    async ({ silent = false } = {}) => {
      if (!workspaceId || !teamId) return;
      try {
        if (!silent) setLoading(true);
        const [overviewRes, chartRes] = await Promise.all([
          betaAPI.getTeamOverview({
            workspaceId,
            teamId,
            queryParams: { ...filterParams, page_size: PLAYERS_PAGE_SIZE },
          }),
          betaAPI.getTeamLoadChart({
            workspaceId,
            teamId,
            queryParams: periodParams,
          }),
        ]);

        const overview = overviewRes.data;

        // Map players (paginated: { results: [...], page, ... })
        const playersArray =
          overview.players?.results || overview.players || [];

        // Compute team-wide averages to use as fallbacks for missing API fields
        const computedTotalImpacts = playersArray.reduce(
          (sum, p) => sum + (p.total_impacts ?? 0),
          0
        );
        const computedAvgLoad =
          playersArray.length > 0
            ? Math.round(
                playersArray.reduce((sum, p) => sum + (p.avg_load ?? 0), 0) /
                  playersArray.length
              )
            : 0;
        const teamAvgImpacts =
          playersArray.length > 0
            ? playersArray.reduce((sum, p) => sum + (p.total_impacts ?? 0), 0) /
              playersArray.length
            : 0;

        // Map aggregate stats (use computed values as fallbacks)
        setTeamStats({
          seasonLoad: overview.aggregate_stats?.avg_load ?? 0,
          avgLoadPerPlayer:
            overview.aggregate_stats?.avg_load_per_player ?? computedAvgLoad,
          totalEvents:
            overview.aggregate_stats?.total_impacts ?? computedTotalImpacts,
          aboveThresholdAvg: overview.aggregate_stats?.above_threshold_avg ?? 0,
          impactsAvg: overview.aggregate_stats?.impacts_avg ?? 0,
          peakImpact: overview.aggregate_stats?.peak_impact ?? null,
          playersNeedingAttention:
            overview.aggregate_stats?.players_needing_attention ?? 0,
          monitoringCount: overview.aggregate_stats?.monitoring_count ?? 0,
          reviewCount: overview.aggregate_stats?.review_count ?? 0,
        });

        // load_vs_peers is the API's like-for-like comparison (sessions where
        // the player and at least one peer both have data, both sides averaged
        // over that same set). The list badge and the selected-player banner
        // both display it verbatim, so they can never disagree.
        teamAvgImpactsRef.current = teamAvgImpacts;
        setPlayers(playersArray.map((p) => mapPlayer(p, teamAvgImpacts)));
        setPlayersMeta({
          totalCount: overview.players?.total_count ?? playersArray.length,
          page: overview.players?.page ?? 1,
          totalPages: overview.players?.total_pages ?? 1,
        });

        // Map team load chart data (paginated: { data_points: { results: [...] } })
        const chartArray =
          chartRes.data?.data_points?.results ||
          chartRes.data?.data_points ||
          [];
        setTeamLoadData(
          chartArray.map((d) => ({
            name: d.date || d.name || "",
            sessionName: d.session_name || d.name || "",
            type: d.type || "training",
            teamLoad: d.avg_load ?? d.team_load ?? 0,
            ...teamLoadPoint(d),
          }))
        );
      } catch (err) {
        console.error("Failed to fetch team data", err);
        showToast.error(t("team.overview.toasts.loadFailed"));
      } finally {
        setLoading(false);
      }
    },
    [workspaceId, teamId, periodParams, filterParams, t]
  );

  const initialLoadDone = useRef(false);

  useEffect(() => {
    if (initialLoadDone.current) {
      fetchTeamData({ silent: true });
    } else {
      fetchTeamData().then(() => {
        initialLoadDone.current = true;
      });
    }
  }, [fetchTeamData]);

  // Append the next page of athletes (same period/type filters). Deduped by
  // id in case a page boundary shifted between requests.
  const loadMorePlayers = useCallback(async () => {
    if (playersLoadingMore || playersMeta.page >= playersMeta.totalPages)
      return;
    setPlayersLoadingMore(true);
    try {
      const nextPage = playersMeta.page + 1;
      const res = await betaAPI.getTeamOverview({
        workspaceId,
        teamId,
        queryParams: {
          ...filterParams,
          page: nextPage,
          page_size: PLAYERS_PAGE_SIZE,
        },
      });
      const pageData = res.data?.players || {};
      const batch = pageData.results || [];
      setPlayers((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        return [
          ...prev,
          ...batch
            .filter((p) => !seen.has(p.id))
            .map((p) => mapPlayer(p, teamAvgImpactsRef.current)),
        ];
      });
      setPlayersMeta((prev) => ({
        totalCount: pageData.total_count ?? prev.totalCount,
        page: pageData.page ?? nextPage,
        totalPages: pageData.total_pages ?? prev.totalPages,
      }));
    } catch (err) {
      console.error("Failed to load more athletes", err);
    } finally {
      setPlayersLoadingMore(false);
    }
  }, [playersLoadingMore, playersMeta, workspaceId, teamId, filterParams]);

  // Fetch player load chart when a player is selected. `playerLoading` drives a
  // loading state for the chart + load-dependent cards so switching between
  // players shows a skeleton instead of flashing the previous player's numbers
  // (or a 0% peer comparison) before the new data lands.
  useEffect(() => {
    if (!selectedPlayerId || !workspaceId || !teamId) {
      setPlayerLoadData([]);
      setPlayerLoading(false);
      return;
    }

    let cancelled = false;
    setPlayerLoading(true);

    const fetchPlayerChart = async () => {
      try {
        const res = await betaAPI.getPlayerLoadChart({
          workspaceId,
          teamId,
          playerId: selectedPlayerId,
          queryParams: periodParams,
        });

        // Paginated: { data_points: { results: [...] } }
        const chartArray =
          res.data?.data_points?.results || res.data?.data_points || [];
        if (cancelled) return;
        setPlayerLoadData(
          chartArray.map((d) => ({
            name: d.date || d.name || "",
            sessionName: d.session_name || d.name || "",
            type: d.type || "training",
            playerLoad: d.player_load ?? 0,
            peerAvg: d.peer_avg_load ?? 0,
            // Had load data in this session (counts towards impact load).
            played: d.player_load != null,
            // Was in the session at all, with or without impacts (SPO-1537).
            attended: d.attended ?? d.player_load != null,
          }))
        );
        setPlayerLoadFor(selectedPlayerId);
      } catch (err) {
        console.error("Failed to fetch player chart", err);
      } finally {
        if (!cancelled) setPlayerLoading(false);
      }
    };

    fetchPlayerChart();
    return () => {
      cancelled = true;
    };
  }, [selectedPlayerId, workspaceId, teamId, periodParams]);

  // Team-wide header counts come from the server-side aggregate endpoint in a
  // single call (period + type as query params). No client-side counting and
  // no per-session fan-out, so teams with >100 sessions are no longer capped.
  useEffect(() => {
    if (!tracksHeaders || !workspaceId || !teamId) {
      setHeaderStats({ total: 0, perSession: [], loading: false });
      return;
    }

    let cancelled = false;
    setHeaderStats((s) => ({ ...s, loading: true }));

    const fetchHeaderStats = async () => {
      try {
        // threshold_g omitted → the endpoint defaults to 10, matching
        // DEFAULT_HEADER_THRESHOLD_G. Only send it if the UI exposes a value.
        const res = await betaAPI.getTeamHeaderStats({
          workspaceId,
          teamId,
          queryParams: filterParams,
        });

        const sessions = res.data?.sessions || [];
        const perSession = sessions
          .map((s) => ({
            name: s.date,
            date: s.date,
            sessionName: s.name || t("team.overview.fallbackSessionName"),
            type: s.type || "training",
            headers: s.headers ?? 0,
          }))
          .sort((a, b) => new Date(a.date) - new Date(b.date));

        if (!cancelled)
          setHeaderStats({
            total: res.data?.total_headers ?? 0,
            perSession,
            loading: false,
          });
      } catch (err) {
        console.error("Failed to fetch team header stats", err);
        if (!cancelled)
          setHeaderStats({ total: 0, perSession: [], loading: false });
      }
    };

    fetchHeaderStats();
    return () => {
      cancelled = true;
    };
  }, [tracksHeaders, workspaceId, teamId, filterParams, t]);

  // Get selected player object
  const selectedPlayer = players.find((p) => p.id === selectedPlayerId);
  // Loading from the moment a player is selected until *their* data lands —
  // not just while the request is in flight.
  const playerChartLoading =
    !!selectedPlayerId && (playerLoading || playerLoadFor !== selectedPlayerId);

  const handleBack = () => {
    navigate(`/v2/${workspaceId}`);
  };

  const handleTeamUpdate = async (data) => {
    try {
      await betaAPI.updateTeam({
        workspaceId,
        teamId,
        body: data,
      });
      showToast.success(t("team.overview.toasts.teamUpdated"));
      fetchTeam();
      fetchTeamData();
    } catch (err) {
      console.error("Failed to update team:", err);
      showToast.error(t("team.overview.toasts.teamUpdateFailed"));
    }
  };

  const handleTeamDeleted = async () => {
    try {
      await betaAPI.deleteTeam({
        workspaceId,
        teamId,
      });
      showToast.success(t("team.overview.toasts.teamDeleted"));
      navigate(`/v2/${workspaceId}`);
    } catch (err) {
      console.error("Failed to delete team:", err);
      showToast.error(t("team.overview.toasts.teamDeleteFailed"));
    }
  };

  const handleAddPlayer = useCallback(() => {
    setShowAddPlayerModal(true);
  }, []);

  const handleCreatePlayer = async (playerData, { createMore } = {}) => {
    try {
      await betaAPI.createPlayer({
        workspaceId,
        teamId,
        body: playerData,
      });
      if (!createMore) {
        showToast.success(t("team.overview.toasts.athleteAdded"));
        setShowAddPlayerModal(false);
      }
      fetchTeamData({ silent: true });
    } catch (err) {
      console.error("Failed to create player:", err);
      showToast.error(t("team.overview.toasts.athleteAddFailed"));
    }
  };

  const handleUpdatePlayer = async (playerData) => {
    if (!selectedPlayerId) return;
    try {
      await betaAPI.updatePlayer({
        workspaceId,
        teamId,
        playerId: selectedPlayerId,
        body: playerData,
      });
      showToast.success(t("team.overview.toasts.athleteUpdated"));
      setShowEditPlayerModal(false);
      fetchTeamData();
    } catch (err) {
      console.error("Failed to update player:", err);
      showToast.error(t("team.overview.toasts.athleteUpdateFailed"));
    }
  };

  const handleDeletePlayer = async () => {
    if (!selectedPlayerId) return;
    try {
      const { data } = await betaAPI.deletePlayer({
        workspaceId,
        teamId,
        playerId: selectedPlayerId,
      });
      // Under-16 teams keep the data for 30 days (a parent can ask for a
      // copy) and tell us when it goes.
      if (data?.deletion_scheduled_for) {
        showToast.success(
          t("team.overview.toasts.athleteRemovedDeletionScheduled", {
            date: new Date(data.deletion_scheduled_for).toLocaleDateString(
              dateLocale("en-GB"),
              { day: "numeric", month: "long" }
            ),
          })
        );
      } else {
        showToast.success(t("team.overview.toasts.athleteDeleted"));
      }
      setShowDeletePlayerModal(false);
      setSelectedPlayerId(null);
      setSearchParams({}, { replace: true });
      fetchTeamData();
    } catch (err) {
      console.error("Failed to delete player:", err);
      showToast.error(t("team.overview.toasts.athleteDeleteFailed"));
    }
  };

  const handleSwitchTeam = async (newTeamId) => {
    if (!selectedPlayerId) return;
    try {
      await betaAPI.changePlayerTeam({
        workspaceId,
        teamId,
        playerId: selectedPlayerId,
        newTeamId,
      });
      showToast.success(t("team.overview.toasts.athleteMoved"));
      setShowSwitchTeamModal(false);
      setSelectedPlayerId(null);
      setSearchParams({}, { replace: true });
      fetchTeamData();
    } catch (err) {
      console.error("Failed to switch team:", err);
      showToast.error(t("team.overview.toasts.athleteMoveFailed"));
    }
  };

  // Update player selection and sync to URL
  const updateSelectedPlayer = useCallback(
    (playerId) => {
      setSelectedPlayerId(playerId);
      if (playerId) {
        setSearchParams({ player: playerId }, { replace: true });
      } else {
        setSearchParams({}, { replace: true });
      }
    },
    [setSearchParams]
  );

  // Toggle player selection (click again to deselect)
  const handlePlayerSelect = useCallback(
    (player) => {
      const newPlayerId = player.id === selectedPlayerId ? null : player.id;
      updateSelectedPlayer(newPlayerId);
    },
    [selectedPlayerId, updateSelectedPlayer]
  );

  // Keyboard navigation for cycling through players (respects current sort/filter)
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
      // The athlete list only exists on the Impact load tab; elsewhere the
      // arrow keys should scroll the page as normal.
      if (activeTab !== "impact-load") return;
      if (!sortedPlayers.length) return;

      e.preventDefault();

      const currentIndex = selectedPlayerId
        ? sortedPlayers.findIndex((p) => p.id === selectedPlayerId)
        : -1;

      let newIndex;
      if (e.key === "ArrowDown") {
        newIndex =
          currentIndex < sortedPlayers.length - 1 ? currentIndex + 1 : 0;
      } else {
        newIndex =
          currentIndex > 0 ? currentIndex - 1 : sortedPlayers.length - 1;
      }

      updateSelectedPlayer(sortedPlayers[newIndex].id);
    },
    [activeTab, selectedPlayerId, sortedPlayers, updateSelectedPlayer]
  );

  // Add keyboard event listener
  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Filter chart data by session type (client-side fallback if API doesn't filter)
  const filteredTeamLoadData = useMemo(() => {
    if (sessionType === "all") return teamLoadData;
    return teamLoadData.filter((d) => d.type === sessionType);
  }, [teamLoadData, sessionType]);

  const filteredPlayerLoadData = useMemo(() => {
    if (sessionType === "all") return playerLoadData;
    return playerLoadData.filter((d) => d.type === sessionType);
  }, [playerLoadData, sessionType]);

  // Chart data switches between team and player data
  const chartData = selectedPlayerId
    ? filteredPlayerLoadData
    : filteredTeamLoadData;

  // Impact load for the stat card (SPO-1534): per athlete per session, a real
  // total, and how many sessions had data. Sessions without impact data are
  // left out. For a selected athlete, only sessions they played count
  // (attendance is covered by the Attendance tab).
  const loadStats = useMemo(
    () =>
      selectedPlayerId
        ? impactLoadStats(filteredPlayerLoadData, { player: true })
        : impactLoadStats(filteredTeamLoadData),
    [filteredTeamLoadData, filteredPlayerLoadData, selectedPlayerId]
  );

  // Peer comparison stats for the selected player. The load comparison comes
  // straight from the overview API's load_vs_peers/peer_avg_load — the same
  // values the list badge shows — rather than being recomputed from chart
  // data, so the two can never drift apart. loadDiff is null when the player
  // shared fewer than 3 sessions with teammates (comparison hidden).
  const peerStats = useMemo(() => {
    if (!selectedPlayer) return null;

    const loadDiff = selectedPlayer.loadVsPeers ?? null;
    const peerAvgLoad = selectedPlayer.peerAvgLoad ?? 0;

    const peerAvgEvents = selectedPlayer.peerAvgEvents;
    const eventsDiff =
      peerAvgEvents > 0
        ? Math.round(
            ((selectedPlayer.events - peerAvgEvents) / peerAvgEvents) * 100
          )
        : 0;

    return {
      loadDiff,
      eventsDiff,
      peerAvgLoad: Math.round(peerAvgLoad),
      peerSessions: selectedPlayer.peerSessions ?? 0,
    };
  }, [selectedPlayer]);

  if (loading || !team) {
    const rowSkeleton = (i, avatar = "h-8 w-8 rounded-full") => (
      <div
        key={i}
        className="flex animate-pulse items-center justify-between border-b border-[#F3F4F6] px-4 py-3"
      >
        <div className="flex items-center gap-3">
          <div className={`${avatar} bg-[#E5E7EB]`} />
          <div>
            <div className="mb-1.5 h-3.5 w-24 rounded bg-[#E5E7EB]" />
            <div className="h-3 w-16 rounded bg-[#E5E7EB]" />
          </div>
        </div>
        <div className="h-4 w-12 rounded bg-[#E5E7EB]" />
      </div>
    );
    return (
      <PageFrame>
        {/* Hero + tabs skeleton — same footprint as the loaded header */}
        <div
          className="flex-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(232, 133, 74, 0.08) 0%, rgba(232, 133, 74, 0.03) 50%, rgba(255, 255, 255, 0) 100%)",
          }}
        >
          <div className="mx-auto max-w-[1440px] animate-pulse px-6 pb-4 pt-5">
            <div className="mb-4 h-4 w-16 rounded bg-[#E5E7EB]" />
            <div className="mb-2 flex items-center gap-3">
              <div className="h-7 w-48 rounded bg-[#E5E7EB]" />
              <div className="h-5 w-14 rounded bg-[#E5E7EB]" />
            </div>
            <div className="h-4 w-24 rounded bg-[#E5E7EB]" />
          </div>
          <div className="mx-auto max-w-[1440px] px-6">
            <div className="flex animate-pulse gap-6 border-b border-[#F3F4F6] px-4 py-3.5">
              <div className="h-4 w-20 rounded bg-[#E5E7EB]" />
              <div className="h-4 w-20 rounded bg-[#E5E7EB]" />
            </div>
          </div>
        </div>

        <PaneFrame
          rail={
            <div className="flex h-full flex-col rounded-xl border border-[#F3F4F6] bg-white">
              <div className="flex animate-pulse items-center justify-between border-b border-[#F3F4F6] px-4 py-3">
                <div className="h-4 w-16 rounded bg-[#E5E7EB]" />
                <div className="h-7 w-20 rounded-lg bg-[#E5E7EB]" />
              </div>
              <div className="min-h-0 flex-1 overflow-hidden">
                {[...Array(14)].map((_, i) => rowSkeleton(i))}
              </div>
            </div>
          }
        >
          {/* Overview card skeleton */}
          <div className="rounded-xl border border-[#F3F4F6] bg-white">
            <div className="flex animate-pulse items-center justify-between border-b border-[#F3F4F6] px-4 py-3">
              <div className="h-4 w-28 rounded bg-[#E5E7EB]" />
              <div className="flex items-center gap-2">
                <div className="h-7 w-32 rounded-lg bg-[#E5E7EB]" />
                <div className="h-7 w-28 rounded-lg bg-[#E5E7EB]" />
              </div>
            </div>
            <div className="p-4">
              <div className="mb-4 grid grid-cols-3 gap-3">
                {[...Array(3)].map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse rounded-xl border border-[#F3F4F6] bg-white p-4"
                  >
                    <div className="mb-2 flex items-center gap-1.5">
                      <div className="h-3.5 w-3.5 rounded bg-[#E5E7EB]" />
                      <div className="h-3 w-16 rounded bg-[#E5E7EB]" />
                    </div>
                    <div className="mb-2 h-8 w-20 rounded bg-[#E5E7EB]" />
                    <div className="h-3 w-full rounded bg-[#E5E7EB]" />
                  </div>
                ))}
              </div>
              <div className="h-64 w-full animate-pulse rounded-xl bg-[#E5E7EB]" />
            </div>
          </div>

          {/* Feed card skeleton */}
          <div className="rounded-xl border border-[#F3F4F6] bg-white">
            <div className="px-4 pt-4">
              <div className="h-8 animate-pulse rounded-lg bg-[#F3F4F6]" />
            </div>
            <div className="mt-2">
              {[...Array(4)].map((_, i) =>
                rowSkeleton(i, "h-8 w-8 rounded-lg")
              )}
            </div>
          </div>
        </PaneFrame>
      </PageFrame>
    );
  }

  return (
    <PageFrame>
      {/* Fixed header: hero + tabs. The tab body below fills the rest. */}
      <div className="flex-none">
        {/* Hero Section with gradient - full width background */}
        <div
          style={{
            background:
              "linear-gradient(180deg, rgba(232, 133, 74, 0.08) 0%, rgba(232, 133, 74, 0.03) 50%, rgba(255, 255, 255, 0) 100%)",
          }}
        >
          <div className="mx-auto max-w-[1440px]">
            <TeamHeroSection
              team={team}
              players={players}
              pendingClaims={pendingClaims}
              onClaimsChanged={() => {
                fetchPendingClaims();
                fetchTeamData({ silent: true });
              }}
              onBack={handleBack}
              onTeamUpdate={handleTeamUpdate}
              onTeamDeleted={handleTeamDeleted}
            />
          </div>
        </div>

        {/* Tab bar */}
        <div className="mx-auto max-w-[1440px] px-6">
          <TeamOverviewTabs
            tabs={[
              ...TABS,
              ...(team?.under16
                ? [{ id: "consent", labelKey: "team.overview.tabs.consent" }]
                : []),
            ].map((tab) => ({ id: tab.id, label: t(tab.labelKey) }))}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
        </div>
      </div>

      {/* Tab content */}
      {activeTab === "overview" ? (
        <OverviewTab
          workspaceId={workspaceId}
          teamId={teamId}
          team={team}
          players={players}
          playersTotalCount={playersMeta.totalCount}
          teamLoadData={teamLoadData}
          onOpenTab={setActiveTab}
          onAddPlayer={handleAddPlayer}
        />
      ) : activeTab === "impact-load" ? (
        <ImpactLoadTab
          players={players}
          playersTotalCount={playersMeta.totalCount}
          hasMorePlayers={playersMeta.page < playersMeta.totalPages}
          playersLoadingMore={playersLoadingMore}
          onLoadMorePlayers={loadMorePlayers}
          selectedPlayerId={selectedPlayerId}
          onPlayerSelect={handlePlayerSelect}
          onAddPlayer={handleAddPlayer}
          onSortedPlayersChange={setSortedPlayers}
          selectedPlayer={selectedPlayer}
          sessionType={sessionType}
          setSessionType={setSessionType}
          period={period}
          setPeriod={setPeriod}
          teamLoadData={teamLoadData}
          chartData={chartData}
          playerLoading={playerChartLoading}
          chartDataKey={selectedPlayerId ? "playerLoad" : "teamLoad"}
          loadStats={loadStats}
          teamStats={teamStats}
          team={team}
          peerStats={peerStats}
          setShowEditPlayerModal={setShowEditPlayerModal}
          setShowSwitchTeamModal={setShowSwitchTeamModal}
          setShowDeletePlayerModal={setShowDeletePlayerModal}
          tracksHeaders={tracksHeaders}
          headerStats={headerStats}
        />
      ) : activeTab === "consent" && team?.under16 ? (
        <ConsentTab
          workspaceId={workspaceId}
          teamId={teamId}
          onChanged={() => fetchTeamData({ silent: true })}
        />
      ) : (
        <AttendanceTab players={players} team={team} />
      )}

      {/* Add Player Modal */}
      <AddPlayerModal
        show={showAddPlayerModal}
        onClose={() => setShowAddPlayerModal(false)}
        onCreate={handleCreatePlayer}
        teamName={team?.name || ""}
        teamSport={team?.sport || ""}
      />

      {/* Edit Player Modal */}
      <EditPlayerModal
        show={showEditPlayerModal}
        onClose={() => setShowEditPlayerModal(false)}
        onSave={handleUpdatePlayer}
        player={selectedPlayer}
        teamSport={team?.sport || ""}
      />

      {/* Delete Player Modal */}
      <DeletePlayerModal
        show={showDeletePlayerModal}
        onClose={() => setShowDeletePlayerModal(false)}
        onDelete={handleDeletePlayer}
        player={selectedPlayer}
        under16={!!team?.under16}
      />

      {/* Switch Team Modal */}
      <SwitchTeamModal
        show={showSwitchTeamModal}
        onClose={() => setShowSwitchTeamModal(false)}
        onSwitch={handleSwitchTeam}
        player={selectedPlayer}
        currentTeamId={teamId}
        workspaceId={workspaceId}
      />
    </PageFrame>
  );
};

export default TeamOverview;
